package co.torvus.web.components

import js.coroutines.awaitCancellation
import kotlinx.browser.window
import react.FC
import react.Props
import react.dom.html.ReactHTML.canvas
import react.useEffectOnce
import react.useRef
import web.cssom.ClassName
import web.html.HTMLCanvasElement
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.exp
import kotlin.math.hypot

private const val SPACING = 26.0
private const val BASE_RADIUS = 1.5
private const val INFLUENCE = 130.0

// Easing time constant (ms). Higher = slower/softer ease in & out. ~50ms ≈ a ~100ms settle.
private const val TAU_MS = 100.0
private const val REST_EPSILON = 0.002

private const val BASE_COLOR = "rgba(255, 255, 255, 0.11)"
private val TAU = 2.0 * PI

// Accent orange (#eb780a) that active dots fade toward, from white.
private const val ACCENT_R = 235
private const val ACCENT_G = 120
private const val ACCENT_B = 10

/**
 * A transparent grid of dots on a canvas. Each dot holds a single "intensity" that
 * eases toward a distance-based target every frame, so dots near the cursor grow ~50%
 * and fade toward orange, then smoothly ease back to the grey grid — a ripple that
 * follows the mouse. The rAF loop self-stops once everything settles and restarts on
 * the next move. Used behind the Home hero and the mobile menu.
 */
val DottedBackground = FC<Props> {
    val canvasRef = useRef<HTMLCanvasElement>()

    useEffectOnce {
        val canvas = canvasRef.current ?: return@useEffectOnce
        val parent = canvas.parentElement ?: return@useEffectOnce
        val ctx = canvas.asDynamic().getContext("2d")
        val dpr = window.devicePixelRatio

        var mouseX = -10000.0
        var mouseY = -10000.0
        var width = 0.0
        var height = 0.0
        var cols = 0
        var rows = 0
        var intensity = DoubleArray(0)

        var running = false
        var rafId = 0
        var lastTs = -1.0
        var lastWidth = -1.0

        fun resize() {
            val rect = parent.asDynamic().getBoundingClientRect()
            width = rect.width as Double
            height = rect.height as Double
            canvas.width = (width * dpr).toInt()
            canvas.height = (height * dpr).toInt()
            ctx.setTransform(dpr, 0.0, 0.0, dpr, 0.0, 0.0)
            cols = (width / SPACING).toInt() + 2
            rows = (height / SPACING).toInt() + 2
            if (intensity.size != cols * rows) intensity = DoubleArray(cols * rows)
        }

        // Eases every dot toward its distance-based target by factor [k] and paints.
        // Returns true while any dot is still moving (so the loop keeps running).
        fun stepAndDraw(k: Double): Boolean {
            ctx.clearRect(0.0, 0.0, width, height)
            var moving = false
            var row = 0
            while (row < rows) {
                val gy = SPACING / 2.0 + row * SPACING
                if (gy < height) {
                    var col = 0
                    while (col < cols) {
                        val gx = SPACING / 2.0 + col * SPACING
                        if (gx < width) {
                            val idx = row * cols + col
                            val dist = hypot(gx - mouseX, gy - mouseY)
                            val target = if (dist >= INFLUENCE) {
                                0.0
                            } else {
                                val prox = 1.0 - dist / INFLUENCE
                                prox * prox * (3.0 - 2.0 * prox) // smoothstep spatial falloff
                            }
                            var cur = intensity[idx]
                            cur += (target - cur) * k
                            if (abs(target - cur) > REST_EPSILON) moving = true
                            if (target == 0.0 && cur < REST_EPSILON) cur = 0.0
                            intensity[idx] = cur

                            if (cur <= 0.0) {
                                ctx.beginPath()
                                ctx.arc(gx, gy, BASE_RADIUS, 0.0, TAU)
                                ctx.fillStyle = BASE_COLOR
                                ctx.fill()
                            } else {
                                val radius = BASE_RADIUS * (1.0 + 0.5 * cur)
                                val alpha = 0.11 + (0.45 - 0.11) * cur
                                val red = (255 + (ACCENT_R - 255) * cur).toInt()
                                val green = (255 + (ACCENT_G - 255) * cur).toInt()
                                val blue = (255 + (ACCENT_B - 255) * cur).toInt()
                                ctx.beginPath()
                                ctx.arc(gx, gy, radius, 0.0, TAU)
                                ctx.fillStyle = "rgba($red, $green, $blue, $alpha)"
                                ctx.fill()
                            }
                        }
                        col++
                    }
                }
                row++
            }
            return moving
        }

        fun frame(ts: Double) {
            val dt = if (lastTs < 0.0) 16.0 else ts - lastTs
            lastTs = ts
            val k = (1.0 - exp(-dt / TAU_MS)).coerceIn(0.0, 1.0)
            if (stepAndDraw(k)) {
                rafId = window.requestAnimationFrame(::frame)
            } else {
                running = false
            }
        }

        fun startLoop() {
            if (!running) {
                running = true
                lastTs = -1.0
                rafId = window.requestAnimationFrame(::frame)
            }
        }

        val onMove: (dynamic) -> Unit = { event ->
            val rect = canvas.asDynamic().getBoundingClientRect()
            mouseX = (event.clientX as Double) - (rect.left as Double)
            mouseY = (event.clientY as Double) - (rect.top as Double)
            startLoop()
        }
        val onLeave: (dynamic) -> Unit = {
            mouseX = -10000.0
            mouseY = -10000.0
            startLoop() // let the ripple ease back out
        }
        val onResize: (dynamic) -> Unit = {
            val w = parent.asDynamic().getBoundingClientRect().width as Double
            if (w != lastWidth) {
                lastWidth = w
                resize()
                stepAndDraw(1.0) // snap after a real (width) resize, no animation
            }
        }

        resize()
        lastWidth = width
        stepAndDraw(0.0) // initial grey grid
        parent.asDynamic().addEventListener("mousemove", onMove)
        parent.asDynamic().addEventListener("mouseleave", onLeave)
        window.asDynamic().addEventListener("resize", onResize)

        awaitCancellation {
            if (running) window.cancelAnimationFrame(rafId)
            parent.asDynamic().removeEventListener("mousemove", onMove)
            parent.asDynamic().removeEventListener("mouseleave", onLeave)
            window.asDynamic().removeEventListener("resize", onResize)
        }
    }

    canvas {
        className = ClassName("dot-canvas")
        ref = canvasRef
    }
}
